import React, { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { WidgetProps } from "@rjsf/utils";
import {
  useGetIdvFailureDetailsMutation,
  useGetLinkTokenMutation,
  useGetSesstionDetailsMutation,
} from "../../../store/services/privateApi";
import {
  getIdvFailureReason,
  getIdvFailureReasons,
  IdvFailureReason,
} from "../../../utils/idvFailureReason";

export const PlaidIdentityVerificationWidget: React.FC<WidgetProps> = ({
  onChange,
  value,
  options,
  formContext,
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [verificationComplete, setVerificationComplete] = useState(
    (options?.formData as any)?.plaidIdentityVerification || false
  );
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [failureReason, setFailureReason] = useState<IdvFailureReason | null>(
    null
  );
  const [failureReasons, setFailureReasons] = useState<IdvFailureReason[]>([]);
  const [idvId, setIdvId] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [getSesstionDetails] = useGetSesstionDetailsMutation();
  const [getIdvFailureDetails] = useGetIdvFailureDetailsMutation();
  const [getLinkToken] = useGetLinkTokenMutation();

  const { open, ready } = usePlaidLink({
    token: linkToken,
    language: i18n.language,
    onSuccess: (public_token, metadata) => {
      const { link_session_id } = metadata;
      console.log("Plaid session id :", link_session_id);
      setIdvId(link_session_id);
      setVerificationComplete(true);
    },
    onLoad: () => {
      console.log("Plaid link loaded");
    },
    onExit: (err, metadata) => {
      console.log("Plaid link exited:", err, metadata);
    },
  });

  const retrieveIDVerificationStatus = async () => {
    const res: any = await getSesstionDetails(idvId);
    console.log(res.data);

    if (
      res.data.status.toLowerCase() === "active" ||
      res.data.status.toLowerCase() === "failed"
    ) {
      setVerificationFailed(true);
      setVerificationComplete(false);
      // Ask the backend for the typed, PII-stripped failure reason. The
      // endpoint reads the persisted DB snapshot when available and falls
      // back to a live Plaid call. We never read the raw Plaid response
      // here for the reason - keeps PII out of the client.
      try {
        const failureRes: any = await getIdvFailureDetails(idvId);
        setFailureReason(getIdvFailureReason(failureRes?.data));
        setFailureReasons(getIdvFailureReasons(failureRes?.data));
      } catch (e) {
        setFailureReason(null);
        setFailureReasons([]);
      }
    } else {
      setVerificationComplete(true);
      setFailureReason(null);
      setVerificationFailed(false);
      let images: any = {};
      if (
        res.data.documentary_verification.documents[
          res.data.documentary_verification.documents.length - 1
        ]?.images
      ) {
        const imagesRes =
          res.data.documentary_verification.documents[
            res.data.documentary_verification.documents.length - 1
          ]?.images;
        images.original_front = imagesRes.original_front;
        images.original_back = imagesRes.original_back;
        images.face = imagesRes.face;
      }
      // Extract verified identity data from API response
      const verificationData = {
        identityVerified: true,
        verificationStatus: "completed",
        // Extract personal information if available in response
        ...(res.data.user && {
          verifiedDateOfBirth: res.data.user.date_of_birth,
          verifiedAddress: res.data.user.address
            ? {
                street:
                  res.data.user.address.street +
                  " " +
                  (res.data.user.address.street2 || ""),
                city: res.data.user.address.city,
                state: res.data.user.address.region,
                zip: res.data.user.address.postal_code,
                country: res.data.user.address.country,
              }
            : null,
          verifiedPhoneNumber: res.data.user.phone_number || null,
          document_id_number:
            res.data.documentary_verification.documents[
              res.data.documentary_verification.documents.length - 1
            ]?.extracted_data?.id_number || null,
          document_category:
            res.data.documentary_verification.documents[
              res.data.documentary_verification.documents.length - 1
            ]?.extracted_data?.category || null,
          document_issuing_country:
            res.data.documentary_verification.documents[
              res.data.documentary_verification.documents.length - 1
            ]?.extracted_data?.issuing_country || null,
          document_issuing_state:
            res.data.documentary_verification.documents[
              res.data.documentary_verification.documents.length - 1
            ]?.extracted_data?.issuing_region || null,
          images: images,
        }),
        // Extract document images if available
        ...(Object.keys(images).length > 0 && {
          images: images,
        }),
        // Store verification session details
        plaidVerificationId: res.data.id,
        verificationCompletedAt: new Date().toISOString(),
      };

      // Store verification data in formContext for parent to access
      if (formContext && formContext.updateVerificationData) {
        formContext.updateVerificationData(verificationData);
      }
    }
  };

  useEffect(() => {
    console.log("IDV ID changed:", idvId);
    if (idvId && verificationComplete) {
      retrieveIDVerificationStatus();
    }
  }, [idvId, verificationComplete]);

  const handleStartVerificationv2 = async () => {
    try {
      setIsLoading(true);
      const response: any = await getLinkToken({
        language: i18n.language,
        personId: options.personId,
      });
      console.log(response);
      if (response.data?.link_token) {
        setLinkToken(response.data.link_token);
      }
    } catch (err) {
      setError(t("account.plaidIdentityVerification.errorMessage"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          marginBottom: 2,
          fontSize: "18px",
          color: "text.primary",
        }}
      >
        {t("account.plaidIdentityVerification.title")}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mb: 3,
          color: "text.secondary",
          lineHeight: 1.6,
        }}
      >
        {t("account.plaidIdentityVerification.description")}
      </Typography>

      <Box
        sx={{
          mb: 3,
          pb: 3,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h3SemiBold"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: "15px",
            color: "text.primary",
          }}
        >
          {t("account.plaidIdentityVerification.acceptedDocumentsTitle")}
        </Typography>
        <Box
          component="ul"
          sx={{
            pl: 3,
            m: 0,
            color: "text.secondary",
            "& li": {
              lineHeight: 1.8,
            },
          }}
        >
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            {t("account.plaidIdentityVerification.acceptedDocuments.passport")}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            {t(
              "account.plaidIdentityVerification.acceptedDocuments.driversLicense"
            )}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            {t("account.plaidIdentityVerification.acceptedDocuments.stateId")}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            {t(
              "account.plaidIdentityVerification.acceptedDocuments.residentCard"
            )}
          </Typography>
          <Typography component="li" variant="body2">
            {t(
              "account.plaidIdentityVerification.acceptedDocuments.temporaryVisa"
            )}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {verificationComplete && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: "#f0f9f4",
            "& .MuiAlert-icon": {
              color: "#22c55e",
            },
          }}
        >
          {t("account.plaidIdentityVerification.successMessage")}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartVerificationv2}
          disabled={
            isLoading ||
            verificationComplete ||
            failureReason?.retriable === false
          }
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          sx={{
            minWidth: 200,
            height: 44,
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 1.5,
            boxShadow: verificationComplete
              ? "none"
              : "0 1px 3px rgba(56, 124, 136, 0.3)",
            "&:hover": {
              boxShadow: verificationComplete
                ? "none"
                : "0 2px 6px rgba(56, 124, 136, 0.4)",
            },
          }}
        >
          {isLoading
            ? t("account.plaidIdentityVerification.startingButton")
            : verificationComplete
            ? t("account.plaidIdentityVerification.completeButton")
            : t("account.plaidIdentityVerification.startButton")}
        </Button>
      </Box>

      {verificationFailed && (
        <Box
          sx={{
            mt: 3,
            textAlign: "center",
            color: "error.main",
            fontWeight: 500,
            backgroundColor: "#fef2f2",
            padding: 2,
            borderRadius: 1.5,
            border: "1px solid #fecaca",
          }}
        >
          {failureReasons.length > 1 ? (
            <Box
              component="ul"
              sx={{ textAlign: "left", pl: 3, m: 0, color: "inherit" }}
            >
              {failureReasons.map((r) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={r.code}
                  sx={{ color: "inherit", fontWeight: 500, mb: 0.5 }}
                >
                  {t(`account.plaidIdentityVerification.failure.${r.code}`)}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "inherit", fontWeight: 500 }}
            >
              {"✗ "}
              {failureReason
                ? t(
                    `account.plaidIdentityVerification.failure.${failureReason.code}`
                  )
                : t("account.plaidIdentityVerification.failureMessage")}
            </Typography>
          )}
          {failureReason?.retriable === false && (
            <Typography
              variant="body2"
              sx={{ color: "inherit", mt: 1, fontWeight: 400 }}
            >
              {t("account.plaidIdentityVerification.contactSupport")}{" "}
              <a href="mailto:support@tenantport.com">support@tenantport.com</a>
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};
